<template>
  <div class="app-layout">
    <!-- SIDEBAR -->
    <aside 
      class="sidebar" 
      :class="{ 'sidebar--collapsed': sidebarCollapsed }"
    >
      <div class="sidebar__content">
        <!-- Brand Section -->
        <div class="sidebar__brand">
          <v-avatar color="primary" size="40" :class="sidebarCollapsed ? 'mx-auto' : 'mr-3'">
            <v-icon color="white" icon="mdi-chart-box-multiple" />
          </v-avatar>
          <div v-show="!sidebarCollapsed" class="text-truncate">
            <div class="text-h6 font-weight-bold text-primary">MGI</div>
            <div class="text-caption text-medium-emphasis">Sistema Empresarial</div>
          </div>
          <v-spacer v-show="!sidebarCollapsed" />
          <v-btn
            v-show="!sidebarCollapsed"
            icon
            variant="text"
            size="small"
            @click="sidebarCollapsed = !sidebarCollapsed"
          >
            <v-icon icon="mdi-chevron-left" />
          </v-btn>
        </div>

        <!-- Navigation Menu -->
        <!--
          P13-V7B — the menu offers only what this session can open.
            · RETIRED, no handler: "Dashboard Principal", "Super Admin" (with an "SA" chip shown to a
              group_manager too), "Centro de Reportes", "Configuración", "Notificaciones" — plus the
              "⋮" of the user block and the "⚙" of the top bar below. Seven controls that looked
              clickable and did nothing. The way home is the "Inicio" breadcrumb.
            · "Incidencias" pushed `/incidences`, a path the router has never declared; it now names
              `/incidents`, and it is shown only when that route admits the session.
            · "Gestión de Grupos" / "Gestión de Agencias" are super_admin routes. This page also admits
              group_manager, who used to be sent to /access-denied; each link is shown only when its
              target route admits the session (`useRouteAdmission`, the rule the guard itself applies).
        -->
        <nav class="sidebar__nav" data-testid="org-reports-nav">
          <template v-if="moduleLinks.length > 0">
            <div v-show="!sidebarCollapsed" class="nav-section-title">MÓDULOS</div>

            <div
              v-for="link in moduleLinks"
              :key="link.key"
              class="nav-item"
              :class="{ 'nav-item--collapsed': sidebarCollapsed }"
              :data-testid="`org-reports-nav-${link.key}`"
              @click="router.push(link.to)"
            >
              <v-icon class="nav-item__icon" :icon="link.icon" />
              <span v-show="!sidebarCollapsed" class="nav-item__text">{{ link.label }}</span>
            </div>
          </template>
          <!-- P13-V5A-R5: las insignias "Live", "3" y "7" eran literales fijos, no recuentos; retiradas. -->
        </nav>

        <!-- User Section (Bottom) -->
        <div class="sidebar__user">
          <div class="user-info" :class="{ 'user-info--collapsed': sidebarCollapsed }">
            <v-avatar size="32" color="primary" :class="sidebarCollapsed ? 'mx-auto' : ''">
              <v-icon color="white" icon="mdi-account" />
            </v-avatar>
            <!-- P13-V5A-R5: la identidad estaba escrita a mano como "SuperAdmin" /
                 "admin@mgi.com". Esta ruta admite tambien a group_manager, de modo que un
                 gerente de grupo veia el nombre y el correo de OTRO actor en su propia sesion.
                 Ahora se lee de la unica autoridad de sesion del producto (authStore). -->
            <div v-show="!sidebarCollapsed" class="user-details">
              <div class="text-body-2 font-weight-medium" data-testid="org-reports-user-name">
                {{ sessionName }}
              </div>
              <div class="text-caption text-medium-emphasis" data-testid="org-reports-user-email">
                {{ sessionEmail }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Collapse Button for Collapsed State -->
      <v-btn
        v-show="sidebarCollapsed"
        icon
        variant="text"
        size="small"
        class="collapse-btn"
        @click="sidebarCollapsed = false"
      >
  <v-icon icon="mdi-chevron-right" />
      </v-btn>
    </aside>

    <!-- MAIN CONTENT -->
    <main class="main-content">
      <!-- Top Bar -->
      <header class="top-bar">
        <v-btn
          v-if="sidebarCollapsed"
          icon
          variant="text"
          @click="sidebarCollapsed = false"
          class="mr-2"
        >
          <v-icon icon="mdi-menu" />
        </v-btn>
        
        <!-- Breadcrumbs -->
        <!--
          P13-V7B: "Dashboard" did `router.replace('/super-admin')` for every session. This route also
          admits group_manager, whom that sent to /access-denied. The crumb leads to the session's own
          home (`getRedirectForRole`, the guard's own role → home table).
        -->
        <nav class="breadcrumbs">
          <span
            class="breadcrumb-item breadcrumb-item--clickable"
            data-testid="org-reports-breadcrumb-home"
            @click="navigateToDashboard"
          >Inicio</span>
          <v-icon size="16" class="mx-2" icon="mdi-chevron-right" />
          <span class="breadcrumb-item breadcrumb-item--current">Centro de Reportes</span>
        </nav>

        <v-spacer />

        <v-btn
          icon
          variant="text"
          @click="refreshData"
          :loading="isRefreshing"
          class="mr-2"
          aria-label="Actualizar datos"
        >
          <v-icon icon="mdi-refresh" />
        </v-btn>
      </header>

      <!-- Page Header -->
      <div class="page-header">
        <div class="page-header__content">
          <div>
            <h1 class="page-title">Centro de Reportes Empresarial</h1>
            <p class="page-subtitle">Análisis integral de estructura organizacional y capacidades operativas</p>
          </div>
          <!--
            P13-V5A-R2: this chip used to read "Conectado" unconditionally, with no binding at all —
            it stayed green while every request 500'd. It now reports the ACTUAL state of the data.
          -->
          <v-chip
            color="white"
            :text-color="connectionChip.color"
            variant="elevated"
            data-testid="org-reports-connection"
          >
            <v-icon start :icon="connectionChip.icon" />
            {{ connectionChip.label }}
          </v-chip>
        </div>
      </div>

      <!-- Content Area -->
      <div class="content-area" data-testid="org-reports-content">
        <!--
          P13-V5A-R2 TRUTH STATES. Exactly one of these renders. Before R2 there was no loading,
          empty, error or incompatible state at all: the grids always rendered, and on a total
          backend failure they rendered fabricated literals (3 / 4 / 78% / 75% / 88-100 / 8 …).
          No metric is rendered outside the `ready` state.
        -->
        <section v-if="reportsState === 'loading'" class="content-section" data-testid="org-reports-loading">
          <h2 class="section-title">Métricas Ejecutivas</h2>
          <v-alert type="info" variant="tonal" border="start">
            Cargando reportes organizacionales…
          </v-alert>
        </section>

        <section
          v-else-if="reportsState === 'incompatible'"
          class="content-section"
          data-testid="org-reports-incompatible"
        >
          <h2 class="section-title">Métricas Ejecutivas</h2>
          <v-alert type="warning" variant="tonal" border="start">
            <strong>Reportes no disponibles — contrato de veracidad incompatible.</strong>
            <div>
              La respuesta del servidor no declara <code>{{ R2_TRUTH_CONTRACT }}</code>, por lo que no puede
              garantizarse que las cifras estén acotadas al alcance canónico del actor. No se muestra
              ninguna métrica.
            </div>
          </v-alert>
        </section>

        <section v-else-if="reportsState === 'error'" class="content-section" data-testid="org-reports-error">
          <h2 class="section-title">Métricas Ejecutivas</h2>
          <v-alert type="error" variant="tonal" border="start">
            <strong>No fue posible obtener los reportes organizacionales.</strong>
            <div data-testid="org-reports-error-detail">{{ reportsError }}</div>
          </v-alert>
        </section>

        <!-- KPIs Section -->
        <section v-else class="content-section" data-testid="org-reports-ready">
          <div class="section-header">
            <h2 class="section-title">Métricas Ejecutivas</h2>
            <v-chip size="small" color="primary" variant="tonal" data-testid="org-reports-applied-scope">
              Alcance: {{ appliedScopeLabel }}
            </v-chip>
          </div>
          <div class="kpis-grid">
            <div v-for="metric in executiveMetrics" :key="metric.key" class="kpi-card">
              <div class="kpi-card__header">
                <v-icon :color="metric.color" size="24" :icon="metric.icon" />
                <v-chip
                  v-if="metric.state === 'NO_DATA'"
                  size="small"
                  color="grey"
                  variant="tonal"
                  data-testid="org-metric-no-data"
                >
                  Sin datos
                </v-chip>
              </div>
              <div class="kpi-card__value" :data-testid="`org-metric-${metric.key}`">{{ metric.value }}</div>
              <div class="kpi-card__label">{{ metric.label }}</div>
              <div class="kpi-card__subtitle">{{ metric.subtitle }}</div>
            </div>
          </div>
        </section>

        <!-- Quick Actions -->
        <section class="content-section">
          <div class="section-header">
            <h2 class="section-title">Acciones Rápidas</h2>
            <v-btn color="primary" prepend-icon="mdi-refresh" @click="refreshData" :loading="isRefreshing">
              Actualizar Datos
            </v-btn>
          </div>
          <!-- P13-V7B: each shortcut is rendered only when its route admits this session. -->
          <div v-if="managementLinks.length > 0" class="quick-actions">
            <v-btn
              v-for="link in managementLinks"
              :key="link.key"
              block
              size="large"
              :color="link.color"
              variant="elevated"
              :prepend-icon="link.icon"
              :data-testid="`org-reports-quick-${link.key}`"
              @click="router.push(link.to)"
            >
              {{ link.label }}
            </v-btn>
          </div>
        </section>

        <!-- Reports Section — only rendered when the payload carries the R2 guarantees. -->
        <section v-if="reportsState === 'ready'" class="content-section">
          <div class="section-header">
            <h2 class="section-title">Centro de Generación de Reportes</h2>
          </div>
          <div class="reports-grid">
            <div
              v-for="report in reports"
              :key="report.id"
              class="report-card"
              :data-testid="`org-report-card-${report.id}`"
            >
              <div class="report-card__content">
                <div class="report-card__header">
                  <v-avatar :color="report.color" size="48">
                    <v-icon color="white" :icon="report.icon" />
                  </v-avatar>
                  <div class="report-card__info">
                    <h3 class="report-card__title">{{ report.title }}</h3>
                    <p class="report-card__subtitle">{{ report.subtitle }}</p>
                  </div>
                </div>

                <div class="report-card__metrics">
                  <div v-for="metric in report.metrics" :key="metric.label" class="metric-row">
                    <span class="metric-label">{{ metric.label }}:</span>
                    <span class="metric-value" :data-testid="`org-report-metric-${metric.key}`">
                      {{ metric.value }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="report-card__actions">
                <v-btn
                  :color="report.color"
                  variant="elevated"
                  block
                  prepend-icon="mdi-file-chart"
                  @click="generateReport(report.id)"
                  :loading="generatingReport[report.id] === true"
                >
                  Generar Reporte
                </v-btn>
                <!--
                  P13-V7B: the downloads the product promises for these reports ("descargas en Excel
                  y PDF", Vista Ejecutiva) are real: `GET /api/reports/organizational/export/{excel,pdf}/
                  :reportType` renders the SAME scoped report the card shows, under the same role gate.
                  A refused or failed export is said on the card; nothing is downloaded in its place.
                -->
                <div class="d-flex mt-2" style="gap: 8px;">
                  <v-btn
                    variant="outlined"
                    size="small"
                    class="flex-grow-1"
                    prepend-icon="mdi-file-excel"
                    :loading="exporting[`${report.id}:excel`] === true"
                    :data-testid="`org-report-export-excel-${report.id}`"
                    @click="exportReport(report.id, 'excel')"
                  >
                    Excel
                  </v-btn>
                  <v-btn
                    variant="outlined"
                    size="small"
                    class="flex-grow-1"
                    prepend-icon="mdi-file-pdf-box"
                    :loading="exporting[`${report.id}:pdf`] === true"
                    :data-testid="`org-report-export-pdf-${report.id}`"
                    @click="exportReport(report.id, 'pdf')"
                  >
                    PDF
                  </v-btn>
                </div>
                <div
                  v-if="exportErrors[report.id]"
                  class="text-caption text-error mt-2"
                  :data-testid="`org-report-export-error-${report.id}`"
                >
                  {{ exportErrors[report.id] }}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import OrganizationalReportsService from '@/services/OrganizationalReportsService'
import { useRouteAdmission } from '@/composables/useRouteAdmission'
import { getRedirectForRole } from '@/router/guards'

// Router
const router = useRouter()
const authStore = useAuthStore()
const { admits } = useRouteAdmission()

/**
 * P13-V7B — the links this page may OFFER. Each is kept only when the router admits the current
 * session to its target (`admits`, the same role rule the guard applies), so a group_manager — whom
 * this route admits — is never shown a door that opens onto /access-denied. Visibility only: the guard
 * and the backend still decide every navigation and every read.
 */
const MANAGEMENT_LINK_CANDIDATES = Object.freeze([
  { key: 'groups', label: 'Gestión de Grupos', icon: 'mdi-account-group', color: 'indigo', to: '/super-admin/groups-management' },
  { key: 'agencies', label: 'Gestión de Agencias', icon: 'mdi-office-building', color: 'purple', to: '/super-admin/agencies-management' }
])
const INCIDENTS_LINK = Object.freeze({ key: 'incidents', label: 'Incidencias', icon: 'mdi-alert-circle-outline', color: 'error', to: '/incidents' })

const managementLinks = computed(() => MANAGEMENT_LINK_CANDIDATES.filter(link => admits(link.to)))
const moduleLinks = computed(() => [...MANAGEMENT_LINK_CANDIDATES, INCIDENTS_LINK].filter(link => admits(link.to)))
/** P13-V5A-R5 — identidad real de la sesion; sin sustitutos plausibles si falta. */
const sessionName = computed(() => authStore.user?.fullName || authStore.user?.name || 'Sesion sin identificar')
const sessionEmail = computed(() => authStore.user?.email || '')

/**
 * P13-V5A-R2 — ORGANIZATIONAL REPORTS CONSUMER TRUTH CONTRACT.
 *
 * Before R2 every business value on this page was `<optional chain> || '<literal>'`, and the three
 * requests each carried an inline `.catch(() => null)`, so `Promise.all` could never reject. Two
 * consequences B0B recorded and this repair removes:
 *
 *   1. a backend failure rendered 3 / 4 / 78% / 75% / 88-100 / 8 / 1,250 km² / 2.4/km² as though
 *      they were business facts, beside a hardcoded green "Conectado" chip;
 *   2. more seriously, the reads used camelCase keys (`summary.totalGroups`, `globalEfficiency`,
 *      `coverage`, `regions`, …) that the producer has NEVER emitted — so those literals were shown
 *      even on a fully successful 200. This page had never displayed a real organizational metric.
 *
 * The rules now:
 *   · nothing renders unless the payload declares `meta.truth_contract === 'P13-V5A-R2'` AND carries
 *     `metric_states`. An old backend cannot emit either, so FRONTEND-FIRST is safe: this view fails
 *     closed instead of trusting a payload with no authority guarantee.
 *   · substitution happens ONLY on `null` / `undefined` / an explicit `NO_DATA` state — never on
 *     falsiness. A real 0 renders "0".
 *   · a failure is a visible error state. It is never swallowed, and no metric survives it.
 */
const R2_TRUTH_CONTRACT = 'P13-V5A-R2'
const NO_DATA_LABEL = 'Sin datos'

type ReportsState = 'loading' | 'ready' | 'error' | 'incompatible'

// State
const sidebarCollapsed = ref(false)
const isRefreshing = ref(false)
const reportsState = ref<ReportsState>('loading')
const reportsError = ref<string | null>(null)
const generatingReport = reactive<Record<string, boolean>>({})
const exporting = reactive<Record<string, boolean>>({})
const exportErrors = reactive<Record<string, string | null>>({})

// Real data from API — only ever populated with payloads that passed the contract gate.
const realReportsData = ref<{ structure: any; capacity: any; geographic: any }>({
  structure: null,
  capacity: null,
  geographic: null
})

/**
 * Shape-tolerant extraction. `api-client` unwraps `{ success, data }` in an interceptor, but other
 * clients in this repo hand back `{ data, status }` or a raw axios response. An unrecognised
 * transport returns null so an unknown shape fails closed rather than rendering as "no data".
 */
function extractReportPayload(response: any): any | null {
  const candidates = [response, response?.data, response?.data?.data]
  return (
    candidates.find(
      (candidate) =>
        candidate &&
        typeof candidate === 'object' &&
        !Array.isArray(candidate) &&
        'report_type' in candidate &&
        'meta' in candidate
    ) ?? null
  )
}

/**
 * The metrics this view actually renders, and where the producer publishes them. A payload missing
 * any of them cannot be rendered honestly, so it is refused rather than partially displayed.
 */
const REQUIRED_METRICS: Record<string, { container: string; keys: string[] }> = {
  structure: {
    container: 'summary',
    keys: ['total_groups', 'total_agencies', 'structural_health_score']
  },
  capacity: {
    container: 'system_totals',
    keys: [
      'total_system_capacity',
      'total_active_capacity',
      'average_capacity_per_agency',
      'agencies_without_capacity_data'
    ]
  },
  geographic: {
    container: 'summary',
    keys: ['total_regions', 'total_groups', 'total_agencies']
  }
}

/**
 * STRICT state/value correspondence — the repair for the second directorial blocker.
 *
 * Previously the gate accepted any payload carrying the marker and *some* `metric_states`, and then
 * `renderMetric` also turned a bare `null`/`undefined` into "Sin datos". That silently downgraded a
 * BROKEN CONTRACT into a legitimate-looking absence: `state: 'OK'` with no value rendered exactly
 * like a metric the server had honestly declared unavailable.
 *
 * There is now exactly one admissible pairing:
 *
 *     OK      <-> a finite number (0 included — a real zero is a real value)
 *     NO_DATA <-> null / undefined
 *
 * Anything else — a missing state, an unknown state, `OK` with no value, `NO_DATA` carrying a
 * business figure, a non-numeric metric — is a CONTRACT DEFECT and fails closed as `incompatible`.
 */
function isAdmissibleMetric(state: unknown, value: unknown): boolean {
  if (state === 'OK') return typeof value === 'number' && Number.isFinite(value)
  if (state === 'NO_DATA') return value === null || value === undefined
  return false
}

function validateReportPayload(payload: any, reportType: string): boolean {
  if (!payload || typeof payload !== 'object') return false
  if (payload.report_type !== reportType) return false
  if (payload?.meta?.truth_contract !== R2_TRUTH_CONTRACT) return false

  const states = payload.metric_states
  if (!states || typeof states !== 'object' || Array.isArray(states)) return false

  const spec = REQUIRED_METRICS[reportType]
  if (!spec) return false
  const container = payload[spec.container]
  if (!container || typeof container !== 'object' || Array.isArray(container)) return false

  // Every metric this view renders must be present AND consistent.
  for (const key of spec.keys) {
    if (!(key in states)) return false
    if (!isAdmissibleMetric(states[key], container[key])) return false
  }
  // Every state the server declares must also agree with its own value, including the ones this
  // view does not render — a payload that contradicts itself anywhere is not trustworthy here.
  for (const [key, state] of Object.entries(states)) {
    if (!isAdmissibleMetric(state, container[key])) return false
  }
  return true
}

/**
 * Render a validated metric. `validateReportPayload` has already guaranteed the pairing, so this
 * function no longer needs — and deliberately no longer has — a fallback for a missing value:
 * substituting there is exactly how a contract defect used to become "Sin datos".
 */
function renderMetric(value: unknown, state?: string): string {
  if (state === 'NO_DATA') return NO_DATA_LABEL
  return String(value)
}

const connectionChip = computed(() => {
  switch (reportsState.value) {
    case 'ready':
      return { label: 'Datos reales', icon: 'mdi-check-circle', color: 'primary' }
    case 'loading':
      return { label: 'Cargando…', icon: 'mdi-progress-clock', color: 'primary' }
    case 'incompatible':
      return { label: 'Contrato incompatible', icon: 'mdi-alert', color: 'warning' }
    default:
      return { label: 'Sin conexión con el servidor', icon: 'mdi-alert-circle', color: 'error' }
  }
})

/** The partition the server actually applied, echoed so the numbers are auditable on screen. */
const appliedScopeLabel = computed(() => {
  const scope = realReportsData.value.structure?.scope
  if (!scope) return NO_DATA_LABEL
  const ids = (list: unknown) =>
    list === null ? 'todas' : Array.isArray(list) && list.length ? list.join(', ') : '—'
  return `${scope.mode} · administraciones: ${ids(scope.administration_ids)} · grupos: ${ids(scope.group_ids)}`
})

/**
 * Executive metrics — every value comes from a metric the producer actually emits, paired with the
 * server's own state for it. Nothing is rendered unless the payload passed the contract gate, so
 * there is no "partial" mode in which a literal could stand in for a missing key.
 *
 * The former per-card delta chips (`+0`, `+3%`, `+1%`) are GONE rather than re-pointed: R2 has no
 * time-series producer, and a change figure with no producer is a fabricated business fact.
 */
const executiveMetrics = computed(() => {
  if (reportsState.value !== 'ready') return []
  const structure = realReportsData.value.structure
  const capacity = realReportsData.value.capacity
  const geographic = realReportsData.value.geographic

  return [
    {
      key: 'groups',
      label: 'Total Grupos',
      subtitle: 'Grupos dentro del alcance autorizado',
      icon: 'mdi-account-group',
      color: 'primary',
      state: structure.metric_states.total_groups,
      value: renderMetric(structure.summary.total_groups, structure.metric_states.total_groups)
    },
    {
      key: 'agencies',
      label: 'Total Agencias',
      subtitle: 'Agencias dentro del alcance autorizado',
      icon: 'mdi-office-building',
      color: 'info',
      state: structure.metric_states.total_agencies,
      value: renderMetric(structure.summary.total_agencies, structure.metric_states.total_agencies)
    },
    {
      key: 'capacity',
      label: 'Capacidad Total',
      subtitle: 'Capacidad diaria agregada',
      icon: 'mdi-speedometer',
      color: 'success',
      state: capacity.metric_states.total_system_capacity,
      value: renderMetric(
        capacity.system_totals.total_system_capacity,
        capacity.metric_states.total_system_capacity
      )
    },
    {
      key: 'regions',
      label: 'Regiones',
      subtitle: 'Regiones con agencias geolocalizadas',
      icon: 'mdi-map-marker-radius',
      color: 'warning',
      state: geographic.metric_states.total_regions,
      value: renderMetric(geographic.summary.total_regions, geographic.metric_states.total_regions)
    }
  ]
})

// Reports — same rule, one row per real producer metric.
const reports = computed(() => {
  if (reportsState.value !== 'ready') return []
  const structure = realReportsData.value.structure
  const capacity = realReportsData.value.capacity
  const geographic = realReportsData.value.geographic

  return [
    {
      id: 'structure',
      title: 'Estructura Organizacional',
      subtitle: 'Jerarquía de grupos y agencias',
      icon: 'mdi-sitemap',
      color: 'indigo',
      metrics: [
        {
          key: 'structure-groups',
          label: 'Grupos',
          value: renderMetric(structure.summary.total_groups, structure.metric_states.total_groups)
        },
        {
          key: 'structure-agencies',
          label: 'Agencias',
          value: renderMetric(structure.summary.total_agencies, structure.metric_states.total_agencies)
        },
        {
          key: 'structure-health',
          label: 'Salud estructural',
          // NO_DATA when no structural field has ever been observed — never a numeric 0.
          value: renderMetric(
            structure.summary.structural_health_score,
            structure.metric_states.structural_health_score
          )
        }
      ]
    },
    {
      id: 'capacity',
      title: 'Capacidades del Sistema',
      subtitle: 'Análisis de capacidad operativa',
      icon: 'mdi-speedometer',
      color: 'cyan',
      metrics: [
        {
          key: 'capacity-total',
          label: 'Capacidad total',
          value: renderMetric(
            capacity.system_totals.total_system_capacity,
            capacity.metric_states.total_system_capacity
          )
        },
        {
          key: 'capacity-active',
          label: 'Capacidad activa',
          value: renderMetric(
            capacity.system_totals.total_active_capacity,
            capacity.metric_states.total_active_capacity
          )
        },
        {
          key: 'capacity-average',
          label: 'Promedio por agencia',
          // Undefined over an empty partition — the server says NO_DATA, we render "Sin datos".
          value: renderMetric(
            capacity.system_totals.average_capacity_per_agency,
            capacity.metric_states.average_capacity_per_agency
          )
        },
        {
          // The capacity totals are sums over the agencies whose capacity is KNOWN. Disclosing how
          // many were excluded is what keeps the total interpretable instead of quietly partial.
          key: 'capacity-unknown',
          label: 'Agencias sin dato de capacidad',
          value: renderMetric(
            capacity.system_totals.agencies_without_capacity_data,
            capacity.metric_states.agencies_without_capacity_data
          )
        }
      ]
    },
    {
      id: 'geographic',
      title: 'Distribución Geográfica',
      subtitle: 'Cobertura territorial y análisis regional',
      icon: 'mdi-map-marker-radius',
      color: 'teal',
      metrics: [
        {
          key: 'geographic-regions',
          label: 'Regiones',
          value: renderMetric(geographic.summary.total_regions, geographic.metric_states.total_regions)
        },
        {
          key: 'geographic-groups',
          label: 'Grupos geolocalizados',
          value: renderMetric(geographic.summary.total_groups, geographic.metric_states.total_groups)
        },
        {
          key: 'geographic-agencies',
          label: 'Agencias geolocalizadas',
          value: renderMetric(geographic.summary.total_agencies, geographic.metric_states.total_agencies)
        }
      ]
    }
  ]
})

// Methods
const refreshData = async () => {
  isRefreshing.value = true
  try {
    console.log('🔄 Refrescando datos de reportes organizacionales...')
    await loadAllReportsData()
  } catch (error) {
    console.error('❌ Error refrescando datos:', error)
  } finally {
    isRefreshing.value = false
  }
}

/** Clear every metric. Called on any outcome that is not a fully verified payload set. */
const discardReportData = () => {
  realReportsData.value = { structure: null, capacity: null, geographic: null }
}

const errorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'string' && error) return error
  return 'Error desconocido al obtener los reportes organizacionales.'
}

const loadAllReportsData = async () => {
  if (reportsState.value !== 'loading') reportsState.value = 'loading'
  reportsError.value = null
  try {
    // NO inline `.catch(() => null)`. Swallowing the rejection was the exact mechanism that let
    // fabricated literals stand in for a broken backend: `Promise.all` could never reject.
    const [structureRes, capacityRes, geographicRes] = await Promise.all([
      OrganizationalReportsService.getStructureReport(),
      OrganizationalReportsService.getCapacityReport(),
      OrganizationalReportsService.getGeographicReport()
    ])

    const payloads = {
      structure: extractReportPayload(structureRes),
      capacity: extractReportPayload(capacityRes),
      geographic: extractReportPayload(geographicRes)
    }

    // All three must carry the R2 guarantees AND be internally consistent. A partial acceptance
    // would let one card render real data next to another rendering nothing — indistinguishable
    // from the old fabrication.
    const rejected = Object.entries(payloads)
      .filter(([reportType, payload]) => !validateReportPayload(payload, reportType))
      .map(([reportType]) => reportType)
    if (rejected.length > 0) {
      discardReportData()
      reportsState.value = 'incompatible'
      console.warn(
        `⚠️ Contrato ${R2_TRUTH_CONTRACT} no satisfecho por: ${rejected.join(', ')}. ` +
        'No se renderiza ninguna métrica.'
      )
      return
    }

    realReportsData.value = payloads
    reportsState.value = 'ready'
  } catch (error: unknown) {
    // A failure is a failure: no zeros, no fallback figures, no stale values left on screen.
    discardReportData()
    reportsError.value = errorMessage(error)
    reportsState.value = 'error'
    console.error('❌ Error cargando datos de reportes:', error)
  }
}

const REPORT_FETCHERS: Record<string, () => Promise<any>> = {
  structure: () => OrganizationalReportsService.getStructureReport(),
  capacity: () => OrganizationalReportsService.getCapacityReport(),
  geographic: () => OrganizationalReportsService.getGeographicReport()
}

/**
 * Re-fetch ONE report. The per-card spinner is real state now: it used to be a `loading: false`
 * literal inside a `computed`, so the mutation never reached the DOM and the spinner could not appear.
 *
 * The old error path called `window.alert()` and left the fabricated card values on screen. A
 * rejected or non-conforming refresh now demotes the whole page to its explicit error/incompatible
 * state, because a metric that failed to refresh must not keep displaying its previous number as if
 * it were current.
 */
const generateReport = async (reportId: string) => {
  const fetcher = REPORT_FETCHERS[reportId]
  if (!fetcher) {
    console.error(`❌ Tipo de reporte desconocido: ${reportId}`)
    return
  }

  generatingReport[reportId] = true
  try {
    const payload = extractReportPayload(await fetcher())
    if (!validateReportPayload(payload, reportId)) {
      discardReportData()
      reportsState.value = 'incompatible'
      return
    }
    realReportsData.value = { ...realReportsData.value, [reportId]: payload }
  } catch (error: unknown) {
    discardReportData()
    reportsError.value = errorMessage(error)
    reportsState.value = 'error'
    console.error(`❌ Error generando reporte ${reportId}:`, error)
  } finally {
    generatingReport[reportId] = false
  }
}

/**
 * P13-V7B — "Excel" / "PDF" of a report card. `OrganizationalReportsService.exportToExcel` /
 * `exportToPDF` request the blob from `/reports/organizational/export/{excel,pdf}/:reportType`; the
 * file is handed to the browser only when the server produced one. A refusal arrives as a Blob too
 * (the request asked for one), so its JSON body is read to say WHY on the card.
 */
const EXPORTERS: Record<'excel' | 'pdf', { run: (type: string) => Promise<Blob>; ext: string }> = {
  excel: { run: (type) => OrganizationalReportsService.exportToExcel(type), ext: 'xlsx' },
  pdf: { run: (type) => OrganizationalReportsService.exportToPDF(type), ext: 'pdf' }
}

const exportRefusal = async (error: any): Promise<string> => {
  const status = error?.response?.status
  if (status === 403) return 'El servidor no le concede esta exportación.'
  let body: any = error?.response?.data
  if (body && typeof body.text === 'function') {
    try { body = JSON.parse(await body.text()) } catch { body = null }
  }
  const reason = body && (body.error || body.message)
  return reason ? `No se pudo exportar: ${reason}` : 'No se pudo exportar el reporte.'
}

const exportReport = async (reportId: string, format: 'excel' | 'pdf') => {
  const exporter = EXPORTERS[format]
  if (!exporter || !REPORT_FETCHERS[reportId]) return
  const busyKey = `${reportId}:${format}`
  exporting[busyKey] = true
  exportErrors[reportId] = null
  try {
    const blob = await exporter.run(reportId)
    const filename = `MGI_Reporte_${reportId}_${new Date().toISOString().split('T')[0]}.${exporter.ext}`
    OrganizationalReportsService.downloadFile(blob, filename)
  } catch (error: unknown) {
    exportErrors[reportId] = await exportRefusal(error)
  } finally {
    exporting[busyKey] = false
  }
}

// Navigation Methods
const navigateToDashboard = () => {
  // P13-V7B: the session's own home, not the vendor's (see the breadcrumb).
  router.push(getRedirectForRole(String(authStore.userRole || '')))
}

// Lifecycle
onMounted(async () => {
  console.log('🚀 Centro de Reportes Empresarial iniciado - Cargando datos reales...')
  await loadAllReportsData()
})
</script>

<style scoped>
/* Animations */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Layout Principal */
.app-layout {
  display: flex;
  min-height: 100vh;
  background: #f8fafc;
}

/* SIDEBAR */
.sidebar {
  width: 280px;
  background: white;
  border-right: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.sidebar--collapsed {
  width: 72px;
}

.sidebar__content {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Brand Section */
.sidebar__brand {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  min-height: 72px;
}

/* Navigation */
.sidebar__nav {
  flex: 1;
  padding: 16px 0;
  overflow-y: auto;
}

.nav-section-title {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 8px 16px 4px 16px;
  margin-bottom: 4px;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  margin: 2px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.nav-item:hover {
  background: rgba(25, 118, 210, 0.08);
}

.nav-item--active {
  background: rgba(25, 118, 210, 0.12);
  color: #1976d2;
}

.nav-item--active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #1976d2;
  border-radius: 0 2px 2px 0;
}

.nav-item--collapsed {
  justify-content: center;
  padding: 12px;
}

.nav-item__icon {
  color: #64748b;
  transition: color 0.2s ease;
  flex-shrink: 0;
}

.nav-item--active .nav-item__icon {
  color: #1976d2;
}

.nav-item__text {
  margin-left: 12px;
  font-weight: 500;
  color: #374151;
  transition: color 0.2s ease;
  flex: 1;
}

.nav-item--active .nav-item__text {
  color: #1976d2;
  font-weight: 600;
}

.nav-item__badge {
  margin-left: auto;
}

.nav-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 8px 16px;
}

/* User Section */
.sidebar__user {
  padding: 16px;
  border-top: 1px solid #e5e7eb;
}

.user-info {
  display: flex;
  align-items: center;
}

.user-info--collapsed {
  justify-content: center;
}

.user-details {
  margin-left: 12px;
  flex: 1;
}

/* Collapse Button */
.collapse-btn {
  position: absolute;
  top: 20px;
  right: -12px;
  background: white !important;
  border: 1px solid #e5e7eb;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* MAIN CONTENT */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* Top Bar */
.top-bar {
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 0 24px;
  height: 64px;
  display: flex;
  align-items: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.breadcrumbs {
  display: flex;
  align-items: center;
  color: #6b7280;
  font-size: 14px;
}

.breadcrumb-item {
  color: #1976d2;
  text-decoration: none;
  transition: color 0.2s ease;
}

.breadcrumb-item:hover {
  color: #1565c0;
}

.breadcrumb-item--clickable {
  cursor: pointer;
  color: #1976d2;
  text-decoration: none;
  transition: color 0.1s ease;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 500;
}

.breadcrumb-item--clickable:hover {
  color: #1565c0;
  background-color: rgba(25, 118, 210, 0.08);
}

.breadcrumb-item--current {
  color: #374151;
  font-weight: 500;
}

/* Page Header */
.page-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  padding: 48px 24px;
}

.page-header__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
}

.page-title {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 8px;
}

.page-subtitle {
  font-size: 1.125rem;
  opacity: 0.9;
  margin: 0;
}

/* Content Area */
.content-area {
  flex: 1;
  padding: 24px;
  width: 100%;
  max-width: none;
}

.content-section {
  margin-bottom: 48px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: bold;
  color: #374151;
  margin-bottom: 24px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

/* KPIs Grid */
.kpis-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.kpi-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.kpi-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.kpi-card__value {
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 8px;
  color: #111827;
}

.kpi-card__label {
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 4px;
}

.kpi-card__subtitle {
  font-size: 0.75rem;
  color: #9ca3af;
}

/* Quick Actions */
.quick-actions {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

/* Reports Grid */
.reports-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.report-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

.report-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
}

.report-card__content {
  padding: 24px;
  flex: 1;
}

.report-card__header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.report-card__info {
  margin-left: 16px;
}

.report-card__title {
  font-size: 1.25rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 4px;
}

.report-card__subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.report-card__metrics {
  margin-bottom: 16px;
}

.metric-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.metric-label {
  font-size: 0.875rem;
  color: #6b7280;
}

.metric-value {
  font-weight: bold;
  color: #111827;
}

.report-card__actions {
  padding: 0 24px 24px 24px;
}

/* Responsive */
@media (max-width: 1400px) {
  .kpis-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
  
  .reports-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
}

@media (max-width: 1024px) {
  .page-header__content {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .kpis-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
  
  .reports-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .quick-actions {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .sidebar {
    position: fixed;
    left: -280px;
    z-index: 1000;
  }
  
  .sidebar--collapsed {
    left: -72px;
  }
  
  .content-area {
    padding: 16px;
  }
  
  .page-header {
    padding: 32px 16px;
  }
  
  .top-bar {
    padding: 0 16px;
  }
  
  .kpis-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .quick-actions {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}

/* Sidebar show/hide en mobile */
@media (max-width: 768px) {
  .sidebar.sidebar--mobile-open {
    left: 0;
  }
  
  .sidebar.sidebar--mobile-open.sidebar--collapsed {
    left: 0;
    width: 72px;
  }
}
</style>
